import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { ApiClientError } from './client'

export interface LocationLike {
  pathname: string
  search: string
}

/** True for the two errors that mean "you are not allowed in yet", not "something broke". */
export function isAuthError(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    ((error.status === 401 && error.code === 'unauthorized') ||
      error.code === 'password_change_required')
  )
}

/**
 * Where an API error should send the viewer, or null to let the page handle it.
 *   401 unauthorized          → /login?next=<where they were>
 *   password_change_required  → /welcome
 * Returns null when already on that page, so concurrent failures never loop.
 */
export function authRedirectFor(error: unknown, location: LocationLike): string | null {
  if (!(error instanceof ApiClientError)) return null
  if (error.status === 401 && error.code === 'unauthorized') {
    if (location.pathname === '/login') return null
    const here = `${location.pathname}${location.search}`
    const keep = here !== '/' && location.pathname !== '/welcome'
    return keep ? `/login?next=${encodeURIComponent(here)}` : '/login'
  }
  if (error.code === 'password_change_required') {
    return location.pathname === '/welcome' ? null : '/welcome'
  }
  return null
}

/**
 * The app's QueryClient. Every query and mutation error passes through `authRedirectFor`, so a
 * signed-out or must-change-password viewer is sent to the right page from one place. Accounts
 * off (Cloudflare Access, local dev) never produce these errors, so nothing changes there.
 */
export function createQueryClient(
  navigate: (to: string) => void,
  getLocation: () => LocationLike = () => window.location,
): QueryClient {
  const onError = (error: unknown) => {
    const to = authRedirectFor(error, getLocation())
    if (to) navigate(to)
  }
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      // offlineFirst: render whatever is cached immediately, then revalidate if a
      // network is present. Lets the installed PWA show last-synced data offline
      // instead of queries sitting in a permanent "paused" state.
      queries: {
        staleTime: 60_000,
        // Retrying a sign-in error only delays the redirect.
        retry: (failureCount, error) => !isAuthError(error) && failureCount < 1,
        networkMode: 'offlineFirst',
      },
    },
  })
}
